import Component from '@glimmer/component';
import { t } from 'ember-intl';
import {
  HdsPageHeader,
  HdsTextDisplay,
  HdsSeparator,
  HdsBadge,
  HdsText,
  HdsCopyButton,
  HdsAlert,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  DefinitionList,
  Flex,
  Time,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { optionForSourceState } from 'core/helpers';
import MutationContainer from 'hcp/components/page/projects/mutation-container';

/**
 *
 * Displays the Project's fields using a definition list.
 *
 *
 * ```
 * <Page::Projects::Settings
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageProjectsSettings
 *
 */
export default class PageProjectsSettings extends Component {
  /**
   * The project model.
   * @argument model
   * @type {Object}
   */
  get project() {
    const { project } = this.args?.model ?? {};
    return project;
  }
  <template>
    <MutationContainer as |Container|>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t "components.page.projects.settings.title"}}
            </PH.Title>
            <PH.IconTile @icon="settings" />
            <PH.Subtitle data-test-subtitle>
              {{@model.project.name}}
            </PH.Subtitle>
            <PH.Actions>
              <Container.Actions @model={{this.project}} />
            </PH.Actions>
          </HdsPageHeader>
        </:header>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="6" @lg="6">
              <HdsTextDisplay
                @tag="h2"
                @size="400"
                @weight="bold"
                @color="strong"
              >
                {{t "components.page.projects.settings.subtitle"}}
              </HdsTextDisplay>

              <HdsSeparator />

              <DefinitionList
                data-test-project-settings-information
                {{boxPadding "md 0 0"}}
                as |DL|
              >
                <DL.Key>{{t
                    "components.page.projects.settings.body.label.status"
                  }}</DL.Key>
                <DL.Value>{{#let
                    (optionForSourceState "PROJECT" @model.project.state)
                    as |option|
                  }}
                    <HdsBadge
                      @color={{option.color}}
                      @icon={{option.icon}}
                      @isIconOnly={{option.isIconOnly}}
                      @size={{option.size}}
                      @text={{t option.text}}
                      @type={{option.type}}
                    />
                  {{/let}}</DL.Value>
                <DL.Key>
                  {{t
                    "components.page.projects.settings.body.label.creation-date"
                  }}
                </DL.Key>
                <DL.Value>
                  <Time
                    @date={{@model.project.createdAt}}
                    @display="friendly-only"
                  />
                </DL.Value>
                <DL.Key>
                  {{t "components.page.projects.settings.body.label.name"}}
                </DL.Key>
                <DL.Value>
                  {{@model.project.name}}
                </DL.Value>
                {{#if @model.project.description}}
                  <DL.Key data-test-project-settings-description>
                    {{t
                      "components.page.projects.settings.body.label.description"
                    }}
                  </DL.Key>
                  <DL.Value>
                    {{@model.project.description}}
                  </DL.Value>
                {{/if}}
                <DL.Key>
                  {{t "components.page.projects.settings.body.label.id"}}
                </DL.Key>
                <DL.Value>
                  <Flex @alignItems="start" @direction="column" @gap="sm">
                    <Flex @alignItems="center" @direction="row" @gap="xs">
                      <HdsText>{{@model.project.id}}</HdsText>
                      <HdsCopyButton
                        @isIconOnly={{true}}
                        @size="small"
                        @text={{@model.project.id}}
                        @textToCopy={{@model.project.id}}
                      />
                    </Flex>
                    <HdsAlert @type="compact" {{boxMargin "xs 0"}} as |A|>
                      <A.Description>
                        {{t
                          "components.page.projects.settings.body.subtext.text"
                        }}
                      </A.Description>
                      <A.Description>
                        <HdsLinkStandalone
                          @route="cloud.orgs.detail.settings"
                          @model={{@model.project.parent.id}}
                          @icon="arrow-right"
                          @iconPosition="trailing"
                          @size="small"
                          @text={{t
                            "components.page.projects.settings.body.subtext.link"
                          }}
                        /></A.Description>
                    </HdsAlert>
                  </Flex>
                </DL.Value>
              </DefinitionList>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
      <Container.Modals />
    </MutationContainer>
  </template>
}
